/**
 * Typed event emitter for domain and application-level events.
 * @agentic @benchmark/core
 */ /**
 * Type-safe event emitter for publishing and subscribing to domain events.
 * @agentic @benchmark/core
 */ class EventEmitter {
    /**
   * Register a handler called whenever a listener throws during publish.
   *
   * @param handler - Receives the error, event name, and payload
   */ onError(handler) {
        this.errorHandler = handler;
    }
    /**
   * Register a handler called after every successful publish.
   *
   * Useful for analytics / observability integrations (e.g. Datadog RUM).
   *
   * @param handler - Receives the event name and payload
   */ onPublish(handler) {
        this.publishHandler = handler;
    }
    /**
   * Subscribe to an event
   *
   * @param eventName - Name of the event to subscribe to
   * @param listener - Callback function to invoke when event is published
   * @returns Unsubscribe function
   */ subscribe(eventName, listener) {
        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, new Set());
        }
        const listeners = this.listeners.get(eventName);
        listeners.add(listener);
        // Return unsubscribe function
        return ()=>{
            this.unsubscribe(eventName, listener);
        };
    }
    /**
   * Unsubscribe from an event
   *
   * @param eventName - Name of the event to unsubscribe from
   * @param listener - Callback function to remove
   */ unsubscribe(eventName, listener) {
        const listeners = this.listeners.get(eventName);
        if (listeners) {
            listeners.delete(listener);
            if (listeners.size === 0) {
                this.listeners.delete(eventName);
            }
        }
    }
    /**
   * Publish an event to all subscribers
   *
   * @param eventName - Name of the event to publish
   * @param payload - Event payload data
   */ publish(eventName, payload) {
        const listeners = this.listeners.get(eventName);
        if (listeners) {
            listeners.forEach((listener)=>{
                try {
                    listener(payload);
                } catch (error) {
                    // Log error but don't throw - allow other listeners to execute
                    console.error(`Error in event listener for ${eventName}:`, error);
                    // Forward to error handler (e.g. Datadog RUM) if registered
                    if (this.errorHandler) {
                        try {
                            this.errorHandler(error, eventName, payload);
                        } catch  {
                        // Swallow handler errors to prevent cascading failures
                        }
                    }
                }
            });
        }
        // Notify publish handler (e.g. Datadog RUM custom actions) if registered
        if (this.publishHandler) {
            try {
                this.publishHandler(eventName, payload);
            } catch  {
            // Swallow handler errors — observability should never break the app
            }
        }
    }
    /**
   * Remove all listeners for a specific event
   *
   * @param eventName - Name of the event (optional, removes all if not provided)
   */ removeAllListeners(eventName) {
        if (eventName) {
            this.listeners.delete(eventName);
        } else {
            this.listeners.clear();
        }
    }
    /**
   * Get the number of listeners for an event
   *
   * @param eventName - Name of the event
   * @returns Number of listeners
   */ listenerCount(eventName) {
        return this.listeners.get(eventName)?.size || 0;
    }
    constructor(){
        this.listeners = new Map();
    }
}
/**
 * Realm-global key under which the singleton emitter is stashed.
 *
 * The in-process event bus is only useful if there is exactly ONE emitter per
 * runtime: writes publish and views subscribe through the same instance. The
 * `@benchmark/core` build (bunchee) emits a separate copy of this module into
 * each package entrypoint (`./events`, `./actions`, `./hooks`, …), so a plain
 * `new EventEmitter()` module singleton is duplicated — writes published via
 * `@benchmark/core/actions` would never reach subscribers registered via
 * `@benchmark/core/events`, silently breaking every event-driven refetch in the
 * bundled app (source-level tests still pass, since they import one copy). We
 * anchor the instance on the realm's global registry via `Symbol.for` so every
 * duplicated module copy resolves to the same underlying emitter.
 */ const GLOBAL_EVENT_EMITTER_KEY = Symbol.for("@benchmark/core/globalEventEmitter");
const eventEmitterGlobal = globalThis;
/**
 * Global event emitter instance for application-wide domain events.
 *
 * Guaranteed to be a single instance per JS realm even when this module is
 * duplicated across bundle entrypoints (see {@link GLOBAL_EVENT_EMITTER_KEY}).
 * @agentic @benchmark/core
 */ const globalEventEmitter = eventEmitterGlobal[GLOBAL_EVENT_EMITTER_KEY] ?? (eventEmitterGlobal[GLOBAL_EVENT_EMITTER_KEY] = new EventEmitter());

/**
 * Domain event type definitions for application-wide event payloads.
 * @agentic @benchmark/core
 */ /**
 * Domain event name constants for type-safe publishing/subscribing.
 * @agentic @benchmark/core
 */ const DomainEventNames = {
    AppointmentCreated: "appointment.created",
    AppointmentUpdated: "appointment.updated",
    CarePlanActivated: "care-plan.activated",
    CarePlanCancelled: "care-plan.cancelled",
    CarePlanCompleted: "care-plan.completed",
    CarePlanCreated: "care-plan.created",
    CarePlanHeld: "care-plan.held",
    CarePlanPaused: "care-plan.paused",
    CarePlanResumed: "care-plan.resumed",
    CarePlanUpdated: "care-plan.updated",
    CarePlanActivityCompleted: "care-plan.activity.completed",
    CareTimeCreated: "care-time.created",
    CareTimeDeleted: "care-time.deleted",
    CareTimeUpdated: "care-time.updated",
    CoCMBillingSummaryCreated: "cocm-billing-summary.created",
    CoCMBillingSummaryUpdated: "cocm-billing-summary.updated",
    CoCMBillingPacketSubmitted: "cocm-billing-packet.submitted",
    CoCMBillingPacketReopened: "cocm-billing-packet.reopened",
    CareTeamCreated: "care-team.created",
    CareTeamUpdated: "care-team.updated",
    ClinicalImpressionCreated: "clinical-impression.created",
    ClinicalImpressionDeleted: "clinical-impression.deleted",
    ClinicalImpressionUpdated: "clinical-impression.updated",
    CommunicationCreated: "communication.created",
    ConditionCreated: "condition.created",
    ConditionUpdated: "condition.updated",
    CommunicationUpdated: "communication.updated",
    CoverageCreated: "coverage.created",
    CoverageUpdated: "coverage.updated",
    EncounterCreated: "encounter.created",
    EncounterUpdated: "encounter.updated",
    FlagCreated: "flag.created",
    FlagUpdated: "flag.updated",
    GoalCreated: "goal.created",
    GoalUpdated: "goal.updated",
    GoalProgressRecorded: "goal.progress.recorded",
    GoalClosed: "goal.closed",
    HealthcareServiceCreated: "healthcare-service.created",
    HealthcareServiceUpdated: "healthcare-service.updated",
    AvailabilityUpdateLogged: "availability-update.logged",
    LibraryCreated: "library.created",
    LibraryUpdated: "library.updated",
    LibraryReviewed: "library.reviewed",
    LibraryEndorsed: "library.endorsed",
    LibraryRetired: "library.retired",
    LocationCreated: "location.created",
    OrganizationCreated: "organization.created",
    ObservationCreated: "observation.created",
    ObservationUpdated: "observation.updated",
    PatientCreated: "patient.created",
    PatientPaused: "patient.paused",
    PatientUnpaused: "patient.unpaused",
    PatientUpdated: "patient.updated",
    PractitionerCreated: "practitioner.created",
    PractitionerRoleCreated: "practitioner-role.created",
    PractitionerRoleUpdated: "practitioner-role.updated",
    PractitionerUpdated: "practitioner.updated",
    QuestionnaireCreated: "questionnaire.created",
    QuestionnaireResponseCreated: "questionnaire-response.created",
    QuestionnaireResponseUpdated: "questionnaire-response.updated",
    QuestionnaireUpdated: "questionnaire.updated",
    RelatedPersonCreated: "related-person.created",
    RelatedPersonUpdated: "related-person.updated",
    ServiceRequestCreated: "service-request.created",
    ServiceRequestUpdated: "service-request.updated",
    TaskCompleted: "task.completed",
    TaskCreated: "task.created",
    TaskUpdated: "task.updated",
    NotificationTaskCreated: "notification-task.created",
    NotificationTaskUpdated: "notification-task.updated",
    UserErrorShown: "user-error.shown"
};

export { DomainEventNames, EventEmitter, globalEventEmitter };
